import { ApiService } from '@supply-mover-2-0/core';
import type { ApiResponseDataModel } from '@supply-mover-2-0/shared';
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { ToastAPI } from '../../services/ToastService';
import { getCartErrorMessage } from '../../utils/cart-error-message';
import { batchCartQueue } from './batchCartQueue';
import { globalCartState } from './globalCartState';
import type { CartOperationModel } from './optimisticCartItem';
import { resolveOptimisticCartItem } from './optimisticCartItem';
import { sharedCartState } from './sharedCartState';

interface CartItem {
	quantity: number;
	// Null when the line has no stored price (PD-16171).
	price: number | string | null;
	originalPrice?: number | string;
}

interface UseCartOperationsProps {
	clientId: string | null;
	onCartUpdate?: (cartData: unknown) => void;
	selectedShippingAddressId?: number | null;
	selectedBillingAddressId?: number | null;
}

export function useCartOperations({
	clientId,
	onCartUpdate,
	selectedShippingAddressId,
	selectedBillingAddressId,
}: UseCartOperationsProps) {
	const [cartItems, setCartItems] = useState<Record<number, CartItem>>({});
	const [hasCart, setHasCart] = useState(false);
	const [isProcessingQueue, setIsProcessingQueue] = useState(false);
	const [itemsRemoving, setItemsRemoving] = useState<Record<number, boolean>>(
		{},
	);
	const [itemsRemoved, setItemsRemoved] = useState<Record<number, boolean>>({});
	const [pendingCartUpdates, setPendingCartUpdates] = useState<
		Record<number, CartItem>
	>({});
	const [isCartLoading, setIsCartLoading] = useState(true);

	// Subscribe to shared cart state
	useEffect(() => {
		if (clientId == null) {
			return;
		}

		const unsubscribe = sharedCartState.subscribe(clientId, (items) => {
			setCartItems(items);
			setHasCart(Object.keys(items).length > 0);
		});

		return unsubscribe;
	}, [clientId]);

	// Subscribe to global cart state
	useEffect(() => {
		const unsubscribe = globalCartState.subscribe(() => {
			setIsProcessingQueue(globalCartState.isActive());
		});

		// Initial check
		setIsProcessingQueue(globalCartState.isActive());

		return unsubscribe;
	}, []);

	const loadCart = useCallback(async () => {
		if (clientId == null) {
			setIsCartLoading(false);

			return;
		}

		setIsCartLoading(true);

		try {
			// Use shared state to load cart
			await sharedCartState.loadCart(clientId);

			// Load the most recent unsubmitted order for this client. The payload
			// may nest the order under a further `data` key; it is forwarded to
			// onCartUpdate as-is.
			const response = await ApiService.apiGet<
				ApiResponseDataModel<{ data?: unknown }>
			>(`/orders/cart/${clientId}`);

			if (response.success && response.data != null) {
				// Call onCartUpdate if provided
				const cartResponse = response.data.data ?? response.data;

				if (onCartUpdate != null) {
					onCartUpdate(cartResponse);
				}
			}
		} catch (err) {
			// Don't show error to user, just fail silently for cart loading
		} finally {
			setIsCartLoading(false);
		}
	}, [clientId, onCartUpdate]);

	// Helper functions to manage per-item removing state
	const setItemRemoving = useCallback((itemId: number, removing: boolean) => {
		setItemsRemoving((prev) => {
			if (removing) {
				return { ...prev, [itemId]: true };
			} else {
				const newState = { ...prev };
				delete newState[itemId];

				return newState;
			}
		});
	}, []);

	const isItemRemoving = useCallback(
		(itemId: number): boolean => itemsRemoving[itemId] ?? false,
		[itemsRemoving],
	);

	// Helper functions to manage per-item removed state
	const setItemRemoved = useCallback((itemId: number, removed: boolean) => {
		setItemsRemoved((prev) => {
			if (removed) {
				return { ...prev, [itemId]: true };
			} else {
				const newState = { ...prev };
				delete newState[itemId];

				return newState;
			}
		});
	}, []);

	const isItemRemoved = useCallback(
		(itemId: number): boolean => itemsRemoved[itemId] ?? false,
		[itemsRemoved],
	);

	// Helper functions to manage pending prices using shared state
	const setPendingPrice = useCallback(
		(itemId: number, price: number) => {
			if (clientId != null) {
				sharedCartState.setPendingPrice(clientId, itemId, price);
			}
		},
		[clientId],
	);

	const clearPendingPrice = useCallback(
		(itemId: number) => {
			if (clientId != null) {
				sharedCartState.clearPendingPrice(clientId, itemId);
			}
		},
		[clientId],
	);

	const getPendingPrice = useCallback(
		(itemId: number): number | undefined => {
			if (clientId == null) {
				return undefined;
			}

			return sharedCartState.getPendingPrice(clientId, itemId);
		},
		[clientId],
	);

	// Queue a cart operation using the batch queue
	const queueCartOperation = useCallback(
		async (operation: CartOperationModel): Promise<unknown> => {
			if (clientId == null) {
				const error = 'No client ID available';
				ToastAPI.show({
					type: 'error',
					text1: 'Error',
					text2: 'Client ID is missing. Please refresh the page.',
					duration: 5000,
				});
				throw new Error(error);
			}

			// Validate clientId is a valid number
			const numericClientId = parseInt(clientId, 10);

			if (isNaN(numericClientId)) {
				const error = 'Invalid client ID format';
				ToastAPI.show({
					type: 'error',
					text1: 'Error',
					text2: 'Invalid client ID. Please refresh the page.',
					duration: 5000,
				});
				throw new Error(error);
			}

			// Optimistically update cart items for immediate UI feedback. An
			// operation marked not optimistic (PD-16171) is shown only once the
			// server accepts it.
			if (
				(operation.type === 'add' || operation.type === 'update') &&
				operation.optimistic !== false
			) {
				const itemId = operation.itemId;
				const optimisticItem = resolveOptimisticCartItem(
					operation,
					sharedCartState.getPendingPrice(clientId, itemId),
					cartItems[itemId],
				);

				if (optimisticItem != null) {
					setPendingCartUpdates((prev) => ({
						...prev,
						[itemId]: optimisticItem,
					}));

					// Update shared state
					sharedCartState.updateCartItem(clientId, itemId, optimisticItem);
				}

				// Set hasCart to true when adding items
				setHasCart(true);
			} else if (operation.type === 'remove') {
				setPendingCartUpdates((prev) => {
					const updated = { ...prev };
					delete updated[operation.itemId];

					return updated;
				});

				// Update shared state
				sharedCartState.updateCartItem(clientId, operation.itemId, null);
			}

			// Start global operation tracking
			globalCartState.startOperation();

			try {
				// Use batch queue system directly
				const response = await batchCartQueue.enqueueOperation(
					numericClientId,
					{
						type: operation.type,
						itemId: operation.itemId,
						quantity: operation.quantity,
						price: operation.price,
					},
					{
						shippingAddressId: selectedShippingAddressId ?? undefined,
						billingAddressId: selectedBillingAddressId ?? undefined,
					},
				);

				// Call onCartUpdate with the response data if available
				if (
					response?.success &&
					response.data != null &&
					onCartUpdate != null
				) {
					onCartUpdate(response.data);
				}

				// Clear pending updates on success
				if (response?.success) {
					setPendingCartUpdates((prev) => {
						const updated = { ...prev };
						delete updated[operation.itemId];

						return updated;
					});
				}

				return response;
			} catch (error: unknown) {
				// Check if this is a cancelled operation (replaced by newer one)
				const errorMessage =
					error instanceof Error ? error.message : String(error);
				const isCancelled = errorMessage.includes('Operation cancelled');

				if (!isCancelled) {
					// Revert optimistic update on error
					if (operation.type === 'remove') {
						// Restore item to cart if remove failed
						setPendingCartUpdates((prev) => ({
							...prev,
							[operation.itemId]: { quantity: 1, price: 0 },
						}));
					} else {
						// Remove optimistic update if add/update failed
						setPendingCartUpdates((prev) => {
							const updated = { ...prev };
							delete updated[operation.itemId];

							return updated;
						});
					}

					// Show error toast — collapse permission errors into a single
					// readable message; otherwise fall back to a clean generic.
					const apiError =
						(error as { response?: { data?: { error?: string } } })?.response
							?.data?.error ??
						errorMessage ??
						'';
					const verbPhrase =
						operation.type === 'add'
							? 'add this item to'
							: operation.type === 'remove'
								? 'remove this item from'
								: 'update this item in';
					ToastAPI.error(getCartErrorMessage(apiError, verbPhrase));

					// Refresh cart to sync with server state
					await loadCart();
				}

				throw error;
			} finally {
				globalCartState.endOperation();
			}
		},
		[
			clientId,
			cartItems,
			onCartUpdate,
			selectedShippingAddressId,
			selectedBillingAddressId,
			loadCart,
		],
	);

	// Optimistic cart update functions. The price is optional: omitted, the
	// server applies the item's resolved price (PD-16171).
	const handleOptimisticCartAdd = useCallback(
		async (
			itemId: number,
			quantity: number,
			price: number | undefined,
			optimistic = true,
		) => {
			try {
				const result = await queueCartOperation({
					type: 'add',
					itemId,
					quantity,
					price,
					optimistic,
				});

				return { success: true, data: result };
			} catch (error: unknown) {
				const errorMessage =
					error instanceof Error ? error.message : 'Failed to add to cart';

				return { success: false, error: errorMessage };
			}
		},
		[queueCartOperation],
	);

	const handleOptimisticCartUpdate = useCallback(
		async (itemId: number, quantity: number, price: number | undefined) => {
			try {
				const result = await queueCartOperation({
					type: 'update',
					itemId,
					quantity,
					price,
				});

				return { success: true, data: result };
			} catch (error: unknown) {
				const errorMessage =
					error instanceof Error ? error.message : 'Failed to update cart';

				return { success: false, error: errorMessage };
			}
		},
		[queueCartOperation],
	);

	const handleOptimisticCartRemove = useCallback(
		async (itemId: number) => {
			try {
				// Start removing animation
				setItemRemoving(itemId, true);

				const result = await queueCartOperation({
					type: 'remove',
					itemId,
				});

				// On success, transition to removed state
				setItemRemoved(itemId, true);
				setItemRemoving(itemId, false);

				// After animation completes, clear the removed state
				setTimeout(() => {
					setItemRemoved(itemId, false);
				}, 600);

				return { success: true, data: result };
			} catch (error: unknown) {
				// On error, revert removing state
				setItemRemoving(itemId, false);
				setItemRemoved(itemId, false);
				const errorMessage =
					error instanceof Error ? error.message : 'Failed to remove from cart';

				return { success: false, error: errorMessage };
			}
		},
		[queueCartOperation, setItemRemoving, setItemRemoved],
	);

	// Create a comprehensive cart change handler for AddToCartButton and PriceInput
	const handleCartChange = useCallback(
		async (operation?: CartOperationModel) => {
			if (operation != null) {
				// Use optimistic operation if provided
				switch (operation.type) {
					// PD-16171 — no $0 price default. An add without a price lets the
					// server resolve the item's price (or refuse an unpriced item);
					// an update without one keeps the line's stored price.
					case 'add':
						return handleOptimisticCartAdd(
							operation.itemId,
							operation.quantity ?? 1,
							operation.price,
							operation.optimistic,
						);
					case 'update':
						return handleOptimisticCartUpdate(
							operation.itemId,
							operation.quantity ?? 0,
							operation.price,
						);
					case 'remove':
						return handleOptimisticCartRemove(operation.itemId);
				}
			} else {
				// Fallback to regular cart load for backward compatibility
				return loadCart();
			}
		},
		[
			handleOptimisticCartAdd,
			handleOptimisticCartUpdate,
			handleOptimisticCartRemove,
			loadCart,
		],
	);

	const returnValue = {
		cartItems,
		hasCart,
		cartOperationQueue: [] as Array<unknown>, // Return empty array for compatibility
		isProcessingQueue,
		isCartLoading,
		loadCart,
		handleCartChange,
		setCartItems,
		setHasCart,
		pendingCartUpdates, // Expose optimistic updates for badge count
		isItemRemoving,
		isItemRemoved,
		setPendingPrice,
		clearPendingPrice,
		getPendingPrice,
	};

	return returnValue;
}

/**
 * The pending (typed, not yet saved) price for one shop item, re-rendering the
 * caller whenever it changes (PD-16171). The CRM shop's Add button uses it to
 * enable itself the moment a rep types a price (even $0.00) for an unpriced item
 * in the neighboring price cell; `getPendingPrice` alone is a snapshot read and
 * would not re-render the button.
 */
export function usePendingPrice(
	clientId: string | null,
	itemId: number | null | undefined,
): number | undefined {
	const subscribe = useCallback(
		(onChange: () => void): (() => void) => {
			if (clientId == null || itemId == null) {
				return () => {};
			}

			return sharedCartState.subscribePendingPrice(clientId, itemId, onChange);
		},
		[clientId, itemId],
	);
	const getSnapshot = useCallback(
		(): number | undefined =>
			clientId == null || itemId == null
				? undefined
				: sharedCartState.getPendingPrice(clientId, itemId),
		[clientId, itemId],
	);

	return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
